/**
 * @file Classifiers.tsx
 * @module Classifiers
 * @description Klasifikatoru definīciju ielāde un kešošana no API.
 *              Klasifikatori pārvērš neapstrādātas enum vērtības (piem., "roadAccident")
 *              cilvēklasāmos latviešu nosaukumos, ko izmanto pārklājumos, hover
 *              padomos un apzīmējumos. Dati tiek ielādēti vienreiz no /api/classifiers/.
 */

import axios from "axios";
import { useEffect } from "react";
import { ENTRYPOINT } from "../map/Endpoints";

/** Response shape from the /api/classificators endpoint */
export interface ClassifierResponse {
	/** Classifier name/identifier */
	classificator: string;
	/** Maps table names to the list of field names this classifier applies to */
	reference_list: { [key: string]: string[] };
	/** Code-to-label mappings */
	values: Value[];
}

/** Individual classifier value entry */
export interface Value {
	/** Parent classifier value for hierarchical classifiers (null if top-level) */
	superclassifier_value: string | null;
	/** Raw value code (matches feature property values) */
	value: string;
	/** Human-readable display label */
	title: string;
}

/**
 * Global classifier cache.
 * Structure: { [tableName]: { [fieldName]: { [valueCode]: displayLabel } } }
 */
export const CLASSIFIERS_NEW: { [tableName: string]: { [key: string]: { [val: string]: string } } } = {};

/** Guard flag to ensure classifiers are only loaded once */
let hasLoadedClassifiers = false;

/**
 * Headless component that fetches classifier data from the backend on mount.
 * Populates the CLASSIFIERS_NEW global cache for use by popup components
 * and the layer switcher. Only fetches once per application lifecycle.
 */
export const ClassifierComponent = () => {
	useEffect(() => {
		if (hasLoadedClassifiers) return;
		hasLoadedClassifiers = true;

		axios.get<ClassifierResponse[]>(ENTRYPOINT + "/api/classificators").then((res) => {
			res.data.forEach((classificator) => {
				const items = classificator.reference_list;
				let values: { [name: string]: string } = {};
				classificator.values.forEach((v) => {
					values[v.value + (v.superclassifier_value ?? "")] = v.title;
				});
				if (Object.keys(values).length > 0) {
					Object.keys(items).forEach((key) => {
						items[key].forEach((fieldName) => {
							CLASSIFIERS_NEW[key] = { ...(CLASSIFIERS_NEW[key] ?? {}), [fieldName]: values };
						});
					});
				}
			});
		});
	}, []);

	return null;
};
